/**
 * Feature Bundles — Mappatura funzionalità business → risorse e permessi tecnici.
 *
 * Ogni bundle rappresenta un gruppo di funzionalità ad alto livello (es. "Gestione Clienti e Lead")
 * e contiene le risorse DB e i permessi che lo compongono.
 *
 * Il `tier` indica il piano minimo che include il bundle:
 *   neural < quantum < matrix
 *   addon = mai incluso nei piani base (sbloccato da subscription_addons)
 *
 * `modules` → vanno in `enabled_modules` del piano (moduli visibili nel menu)
 * `permissionResources` → risorse che contribuiscono SOLO permessi (sotto-risorse, non moduli)
 * `settingsResources` → risorse settings_* che contribuiscono solo permessi
 */

export type BundleTier = 'neural' | 'quantum' | 'matrix' | 'addon';

export interface FeatureBundle {
  id: string;
  name: string;
  tier: BundleTier;
  /** Moduli principali — vanno in enabled_modules E contribuiscono permessi */
  modules: string[];
  /** Risorse aggiuntive che contribuiscono SOLO permessi (non in enabled_modules) */
  permissionResources?: string[];
  /** Risorse settings_* — contribuiscono solo permessi */
  settingsResources?: string[];
  /** Extra permission IDs non derivabili dalle risorse */
  extraPermissionIds?: string[];
  /** Se true, il bundle non appare nella UI di selezione e non viene incluso nella generazione automatica */
  deprecated?: boolean;
  /** Se true, i moduli e settings di questo bundle mostrano un badge "Beta" nei menu */
  beta?: boolean;
  /** Se true, il bundle è incluso nella risoluzione ma non appare nella UI di selezione */
  hidden?: boolean;
}

export const FEATURE_BUNDLES: FeatureBundle[] = [
  // ══════════════ NEURAL (base) ══════════════
  {
    id: 'clients_leads',
    name: 'Clienti, Lead, Contatti, Dashboard, Calendario',
    tier: 'neural',
    modules: ['clients', 'leads', 'contacts', 'dashboard', 'calendar'],
    permissionResources: ['lead_notes', 'lead_sources', 'categories'],
    settingsResources: ['settings_clients', 'settings_categories', 'settings_interest_categories'],
  },
  {
    id: 'deals_templates',
    name: 'Offerte, Preventivi e Template',
    tier: 'neural',
    modules: ['deals'],
    permissionResources: ['templates'],
    settingsResources: ['settings_deals', 'settings_quote_templates'],
  },
  {
    id: 'lead_forms',
    name: 'Lead Forms',
    tier: 'neural',
    modules: ['lead_forms'],
  },
  {
    id: 'whatsapp_email',
    name: 'WhatsApp e Email',
    tier: 'neural',
    modules: ['whatsapp'],
    settingsResources: ['settings_sendgrid', 'settings_whatsapp'],
  },
  {
    id: 'services_payments',
    name: 'Catalogo Servizi e Pagamenti',
    tier: 'neural',
    modules: ['services'],
    permissionResources: ['payment_methods', 'signatures'],
    settingsResources: ['settings_services', 'settings_payment_methods'],
  },
  {
    id: 'google_workspace',
    name: 'Google Workspace',
    tier: 'neural',
    modules: [],
    settingsResources: ['settings_google_workspace'],
  },
  {
    id: 'users_roles',
    name: 'Utenti e Ruoli',
    tier: 'neural',
    modules: [],
    permissionResources: ['users', 'roles'],
    settingsResources: ['settings_users'],
  },

  // ══════════════ NEURAL (hidden — risorse infrastrutturali e trasversali) ══════════════
  {
    id: 'system_internal',
    name: 'Risorse di Sistema',
    tier: 'neural',
    modules: ['documents'],
    permissionResources: ['settings'],
    settingsResources: [
      'settings_whatsapp', 'settings_smtp', 'settings_menu', 'settings_company',
      'settings_import_export', 'settings_search', 'settings_language',
      'settings_partners', 'settings_security', 'settings_legal',
      'settings_subscription',
    ],
    hidden: true,
  },

  // ══════════════ QUANTUM ══════════════
  {
    id: 'projects_tasks',
    name: 'Progetti e Task',
    tier: 'quantum',
    modules: ['projects', 'tasks'],
  },
  {
    id: 'email_signature',
    name: 'Firma Email',
    tier: 'quantum',
    modules: [],
    permissionResources: ['email_signature'],
  },
  {
    id: 'commissions',
    name: 'Provvigioni e Obiettivi Vendita',
    tier: 'quantum',
    modules: ['commissions'],
    permissionResources: ['user_commissions', 'commission_groups', 'commission_reports', 'commission_advanced'],
    settingsResources: ['settings_commissions', 'settings_sales_targets'],
  },
  {
    id: 'custom_fields',
    name: 'Campi Personalizzati',
    tier: 'quantum',
    modules: [],
    permissionResources: ['custom_fields'],
    settingsResources: ['settings_custom_fields'],
  },

  // ══════════════ MATRIX ══════════════
  {
    id: 'service_advanced',
    name: 'Visibilità Gerarchia, Ore/Pacchetti, Template Costi',
    tier: 'matrix',
    modules: [],
    permissionResources: ['service_task_templates', 'service_costs', 'service_cost_templates'],
  },
  {
    id: 'ai_complete',
    name: 'AI Completa + WhatsApp AI Agent',
    tier: 'matrix',
    modules: [],
    permissionResources: [
      'ai_text_generation', 'ai_task_generation', 'ai_suggestions', 'ai_upsell_scanner',
      'ai_insights', 'ai_status_updates', 'ai_whatsapp_agent', 'ai_meeting_bot',
    ],
    settingsResources: ['settings_meeting_bot'],
  },
  {
    id: 'wiki',
    name: 'Wiki',
    tier: 'matrix',
    modules: ['wiki'],
  },
  {
    id: 'surveys',
    name: 'Sondaggi',
    tier: 'matrix',
    modules: ['surveys'],
  },
  {
    id: 'team_chat',
    name: 'Chat Team',
    tier: 'matrix',
    modules: [],
    settingsResources: ['settings_team_chat'],
  },
  {
    id: 'booking_meetings',
    name: 'Booking e Meetings',
    tier: 'matrix',
    modules: ['booking'],
    permissionResources: ['meetings'],
    settingsResources: ['settings_booking'],
  },
  {
    id: 'automations',
    name: 'Automazioni e Workflow',
    tier: 'matrix',
    modules: [],
    settingsResources: ['settings_automation'],
  },
  {
    id: 'billing_costs',
    name: 'Fatturazione (FiC), Costi e Margini',
    tier: 'matrix',
    modules: ['fattureincloud', 'expenses'],
    permissionResources: [
      'invoices', 'client_expenses', 'service_margins',
      'expense_categories', 'financial_dashboard', 'bank_movements',
    ],
    settingsResources: ['settings_fattureincloud'],
  },
  {
    id: 'ticketing_sla',
    name: 'Ticketing e SLA',
    tier: 'matrix',
    modules: ['tickets'],
    permissionResources: ['ticket_types', 'ticket_time_entries', 'ticket_sla_configs'],
  },
  {
    id: 'partners_referral',
    name: 'Partner e Referral',
    tier: 'matrix',
    modules: ['partnerships'],
    permissionResources: ['external_partners', 'partner_tasks', 'referrers'],
  },
  {
    id: 'marketing_intelligence',
    name: 'Marketing Intelligence (Meta/Google Ads)',
    tier: 'matrix',
    modules: ['marketing'],
    settingsResources: ['settings_meta_ads', 'settings_google_ads'],
  },
  {
    id: 'microsoft_workspace',
    name: 'Microsoft Workspace',
    tier: 'matrix',
    modules: [],
    settingsResources: ['settings_microsoft_workspace'],
  },
  {
    id: 'business_central',
    name: 'Business Central',
    tier: 'matrix',
    modules: [],
    settingsResources: ['settings_business_central'],
  },
  {
    id: 'reports_advanced',
    name: 'Report e Dashboard Avanzati',
    tier: 'matrix',
    modules: ['analytics'],
    permissionResources: ['reports', 'dashboards', 'kpis', 'insights'],
  },
  {
    id: 'api_access',
    name: 'API Token e Documentazione API',
    tier: 'matrix',
    modules: [],
    settingsResources: ['settings_api'],
  },
  {
    id: 'marketplace_integrations',
    name: 'Marketplace Integrazioni',
    tier: 'matrix',
    modules: ['marketplace'],
  },

  // ══════════════ DEPRECATED ══════════════
  {
    id: 'bulk_api_mcp',
    name: 'Bulk Delete, API, MCP e Custom Queries (Deprecato)',
    tier: 'matrix',
    modules: [],
    permissionResources: ['api_config', 'custom_queries'],
    settingsResources: ['settings_bulk_delete'],
    deprecated: true,
  },

  // ══════════════ ADDON (mai nei piani base) ══════════════
  {
    id: 'newsletter',
    name: 'Newsletter',
    tier: 'addon',
    modules: ['newsletter'],
    settingsResources: ['settings_newsletter'],
  },
  {
    id: 'inventory_rentals',
    name: 'Magazzino, Noleggi e WooCommerce',
    tier: 'addon',
    modules: ['inventory', 'rentals'],
    permissionResources: [
      'wh_suppliers', 'wh_contractors', 'wh_ddts', 'wh_lots',
      'wh_bom', 'wh_production', 'wh_processing',
      'wh_samples', 'wh_labels', 'wh_disposals', 'wh_traceability',
    ],
    // settings_ecommerce: negozi collegati (Shopify, WooCommerce col motore nuovo); senza, l'Admin non puo' collegare
    settingsResources: ['settings_woocommerce', 'settings_ecommerce'],
  },
];

// ── Risoluzione bundle → permessi per tier ──

const TIER_ORDER: Record<string, number> = { neural: 0, quantum: 1, matrix: 2 };

export interface ResolvedPlan {
  enabled_modules: string[];
  selected_permissions: string[];
}

/**
 * Risolve i bundle applicabili a un dato tier e genera enabled_modules + selected_permissions.
 *
 * @param tier - Il tier del piano (neural, quantum, matrix)
 * @param allPermissions - Tutte le permission dal DB, raggruppate per resource:
 *   Record<string, { id: string; resource: string; action: string }[]>
 */
export function resolveBundlesForTier(
  tier: 'neural' | 'quantum' | 'matrix',
  allPermissions: Record<string, { id: string; resource: string; action: string }[]>
): ResolvedPlan {
  const tierLevel = TIER_ORDER[tier];
  const activeBundles = FEATURE_BUNDLES.filter(b =>
    !b.deprecated && b.tier !== 'addon' && (TIER_ORDER[b.tier] ?? 99) <= tierLevel
  );

  const modules = new Set<string>();
  const permIds = new Set<string>();

  const addPermsForResource = (res: string) => {
    const perms = allPermissions[res] || [];
    for (const p of perms) {
      permIds.add(p.id);
    }
  };

  for (const bundle of activeBundles) {
    // Moduli principali → enabled_modules + permessi
    for (const mod of bundle.modules) {
      modules.add(mod);
      addPermsForResource(mod);
    }

    // Risorse aggiuntive → solo permessi
    for (const res of (bundle.permissionResources || [])) {
      addPermsForResource(res);
    }

    // Settings resources → solo permessi
    for (const res of (bundle.settingsResources || [])) {
      addPermsForResource(res);
    }

    // Extra permission IDs
    for (const id of (bundle.extraPermissionIds || [])) {
      permIds.add(id);
    }
  }

  return {
    enabled_modules: [...modules].sort(),
    selected_permissions: [...permIds].sort(),
  };
}

/**
 * Restituisce i bundle attivi per un dato tier (per visualizzazione).
 */
export function getBundlesForTier(tier: 'neural' | 'quantum' | 'matrix'): FeatureBundle[] {
  const tierLevel = TIER_ORDER[tier];
  return FEATURE_BUNDLES.filter(b =>
    !b.deprecated && !b.hidden && b.tier !== 'addon' && (TIER_ORDER[b.tier] ?? 99) <= tierLevel
  );
}

/**
 * Restituisce tutte le risorse (con permessi) di un bundle — per la UI di drill-down.
 */
export function getBundleResources(bundle: FeatureBundle): string[] {
  return [
    ...bundle.modules,
    ...(bundle.permissionResources || []),
    ...(bundle.settingsResources || []),
  ];
}

/**
 * Restituisce tutti i bundle non-deprecated, non-hidden e non-addon per la UI di selezione.
 */
export function getSelectableBundles(): FeatureBundle[] {
  return FEATURE_BUNDLES.filter(b => !b.deprecated && !b.hidden && b.tier !== 'addon');
}

/**
 * Restituisce i bundle addon.
 */
export function getAddonBundles(): FeatureBundle[] {
  return FEATURE_BUNDLES.filter(b => !b.deprecated && b.tier === 'addon');
}

/**
 * Dato un elenco di moduli sbloccati dagli addon attivi di un'organizzazione,
 * restituisce tutte le risorse (moduli + permissionResources + settingsResources)
 * dei bundle addon corrispondenti.
 *
 * Usato in RoleManagement per includere i permessi addon nel role editor.
 */
export function getAddonResources(activeAddonModules: string[]): string[] {
  if (!activeAddonModules.length) return [];

  const resources = new Set<string>();
  for (const bundle of getAddonBundles()) {
    const hasOverlap = bundle.modules.some(m => activeAddonModules.includes(m));
    if (hasOverlap) {
      for (const r of getBundleResources(bundle)) {
        resources.add(r);
      }
    }
  }
  return [...resources];
}

// ── Beta detection ──

/**
 * Set di moduli (enabled_modules) che appartengono a bundle beta.
 * Usato in Layout.tsx per mostrare badge "Beta" nel menu principale.
 */
export const BETA_MODULES: Set<string> = new Set(
  FEATURE_BUNDLES
    .filter(b => b.beta)
    .flatMap(b => b.modules)
);

/**
 * Set di risorse settings_* che appartengono a bundle beta.
 */
const BETA_SETTINGS_RESOURCES: Set<string> = new Set(
  FEATURE_BUNDLES
    .filter(b => b.beta)
    .flatMap(b => b.settingsResources || [])
);

/**
 * Verifica se un tab ID delle impostazioni è beta.
 * Converte il tab ID (es. "google-workspace") in risorsa settings (es. "settings_google_workspace").
 */
export function isBetaSettingsTab(tabId: string): boolean {
  const settingsResource = `settings_${tabId.replace(/-/g, '_')}`;
  return BETA_SETTINGS_RESOURCES.has(settingsResource);
}
